// src/layout/builders/ProjectEnquiry/ProjectEnquiryTile.jsx
import React, { useEffect, useState } from "react";
import { ClipboardIcon, XMarkIcon as XIcon } from "@heroicons/react/24/outline";
import useProjectEnqStore from "../../../store/builders/useProjectEnqStore";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { motion, AnimatePresence } from "framer-motion"; // For modal animations

// Helper to format date and time
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return "Invalid Date";
  }
};

const ProjectEnquiryTile = () => {
  const {
    enquiries,
    isLoading, // Main list loading
    fetchEnquiries,
    // --- NEW store items for comments modal ---
    modalComments,
    isCommentsLoading,
    isAddingComment,
    fetchEnquiryComments,
    addEnquiryComment,
    // --- END NEW store items ---
  } = useProjectEnqStore();

  const [filter, setFilter] = useState("All");
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // --- NEW STATE for Comments Modal ---
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedEnquiryForComments, setSelectedEnquiryForComments] =
    useState(null);
  const [newCommentText, setNewCommentText] = useState("");
  // --- END NEW STATE ---

  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    const textString = text?.toString() || "";
    const parts = textString.split(
      new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    );
    return parts?.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const filteredEnquiries = enquiries.filter((enquiry) => {
    const searchTerm = searchQuery.toLowerCase();
    const searchableFields = [
      enquiry.customer_name,
      enquiry.customer_ducktail_id,
      enquiry.phone_number,
      enquiry.district,
      enquiry.taluk,
      // REMOVED: enquiry.comments, // This was the main/primary comment
      enquiry.postcode,
      formatDateTime(enquiry.created_at), // Search formatted date/time
    ];
    return searchableFields.some((field) =>
      field?.toString().toLowerCase().includes(searchTerm)
    );
  });

  const groupedEnquiries = filteredEnquiries.reduce((acc, enquiry) => {
    const dateKey = new Date(enquiry.created_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(enquiry);
    return acc;
  }, {});

  const handleCopy = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  useEffect(() => {
    const status =
      filter === "All"
        ? null
        : filter === "Action taken"
        ? "action_taken"
        : "action_not_taken";
    fetchEnquiries(status);
  }, [filter, fetchEnquiries]);

  // --- NEW MODAL HANDLERS for Comments ---
  const handleOpenCommentsModal = (enquiry) => {
    setSelectedEnquiryForComments(enquiry);
    fetchEnquiryComments(enquiry.id); // Fetch comments for this specific enquiry
    setIsCommentModalOpen(true);
  };

  const handleCloseCommentsModal = () => {
    setIsCommentModalOpen(false);
    setSelectedEnquiryForComments(null);
    setNewCommentText("");
    useProjectEnqStore.setState({ modalComments: [] }); // Clear modal comments from store
  };

  const handleAddNewComment = async () => {
    if (!newCommentText.trim() || !selectedEnquiryForComments) return;
    const success = await addEnquiryComment(
      selectedEnquiryForComments.id,
      newCommentText
    );
    if (success) {
      setNewCommentText(""); // Clear textarea on successful add
      // Comments list in modal will refresh due to fetchEnquiryComments in store
      // Main enquiry list will refresh due to fetchEnquiries in store
    }
  };
  // --- END NEW MODAL HANDLERS ---

  return (
    <div className="py-1 min-h-screen">
      <div className="bg-white rounded-xl shadow-sm p-3">
        <h2 className="text-xl font-semibold text-gray-800 mb-5 px-2">
          Project Enquiries
        </h2>
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 pb-3">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filter === "All"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
              onClick={() => setFilter("All")}
            >
              All
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filter === "Action taken"
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
              onClick={() => setFilter("Action taken")}
            >
              Action taken
            </button>
            <button
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filter === "Action not taken"
                  ? "bg-red-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
              onClick={() => setFilter("Action not taken")}
            >
              Action not taken
            </button>
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="px-3 py-1 text-sm border rounded-full w-full md:w-64 bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-1.5 text-center">Time</th>
                <th className="p-1.5 text-center">Customer Name</th>
                <th className="p-1.5 text-center">Ducktail ID</th>
                <th className="p-1.5 text-center">Phone Number</th>
                <th className="p-1.5 text-center">District</th>
                <th className="p-1.5 text-center">Taluk</th>
                {/* REMOVED: <th className="p-1.5 text-center">Primary Comment</th> */}
                <th className="p-1.5 text-center">Postcode</th>
                <th className="p-1.5 text-center">Status</th>{" "}
                {/* Added Status Column */}
                <th className="p-1.5 text-center">Actions</th>{" "}
                {/* Added Actions Column */}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="9" className="text-center py-3 text-sm">
                    {" "}
                    {/* Adjusted colSpan to 9 */}
                    <div className="flex justify-center items-center">
                      <AiOutlineLoading3Quarters className="animate-spin mr-2" />{" "}
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : filteredEnquiries.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-3 text-sm">
                    {" "}
                    {/* Adjusted colSpan to 9 */}
                    No enquiries found
                  </td>
                </tr>
              ) : (
                Object.entries(groupedEnquiries).map(
                  ([date, entries], index) => (
                    <React.Fragment key={index}>
                      <tr>
                        <td
                          colSpan="9"
                          className="px-1.5 py-2 text-left text-gray-500"
                        >
                          {" "}
                          {/* Adjusted colSpan to 9 */}
                          <span className="font-semibold text-sm bg-gray-300 px-2 py-1 rounded-xl">
                            {date}
                          </span>
                        </td>
                      </tr>
                      {entries.map((entry, idx) => (
                        <tr key={entry.id || idx} className="border-t relative">
                          <td className="p-1.5 text-center">
                            {highlightText(
                              new Date(entry.created_at).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              ),
                              searchQuery
                            )}
                          </td>
                          <td className="p-1.5 text-center">
                            {highlightText(entry.customer_name, searchQuery)}
                          </td>
                          <td className="p-1.5 text-center">
                            <div className="flex items-center justify-center gap-1 relative">
                              {highlightText(
                                entry.customer_ducktail_id,
                                searchQuery
                              )}
                              <ClipboardIcon
                                className="w-3 h-3 text-gray-500 cursor-pointer hover:text-black transition-colors duration-200"
                                onClick={() =>
                                  handleCopy(entry.customer_ducktail_id)
                                }
                              />
                              {copiedId === entry.customer_ducktail_id && (
                                <div
                                  className="absolute left-0 -bottom-6 animate-fade-up"
                                  style={{
                                    animation:
                                      "fadeUpAndOut 2s ease-out forwards",
                                  }}
                                >
                                  <div className="bg-green-600 text-white text-xs px-1.5 py-0.5 rounded shadow-lg flex items-center gap-1">
                                    <svg
                                      className="w-2.5 h-2.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                    Copied!
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-1.5 text-center">
                            {highlightText(entry.phone_number, searchQuery)}
                          </td>
                          <td className="p-1.5 text-center">
                            {highlightText(entry.district, searchQuery)}
                          </td>
                          <td className="p-1.5 text-center">
                            {highlightText(entry.taluk, searchQuery)}
                          </td>
                          {/* REMOVED Primary Comment Cell */}
                          <td className="p-1.5 text-center">
                            {highlightText(entry.postcode, searchQuery)}
                          </td>
                          {/* Status Cell */}
                          <td className="p-1.5 text-center">
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                entry.status === "action_taken"
                                  ? "bg-green-100 text-green-700"
                                  : entry.status === "action_not_taken"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {entry.status?.replace("_", " ") || "N/A"}
                            </span>
                          </td>
                          {/* Actions Cell */}
                          <td className="p-1.5 text-center">
                            <button
                              onClick={() => handleOpenCommentsModal(entry)}
                              className="px-2 py-0.5 bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-medium rounded transition-colors duration-200"
                              title="View/Add Comments"
                            >
                              Comments
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Comments History Modal --- */}
      <AnimatePresence>
        {isCommentModalOpen && selectedEnquiryForComments && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
            onClick={handleCloseCommentsModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 w-full max-w-lg relative flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800">
                  Comments for Enquiry:{" "}
                  {selectedEnquiryForComments.customer_name} (ID:{" "}
                  {selectedEnquiryForComments.id})
                </h3>
                <button
                  onClick={handleCloseCommentsModal}
                  disabled={isAddingComment}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
                  aria-label="Close comments modal"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Comments List Area */}
              <div
                className="flex-grow overflow-y-auto pr-2 -mr-2 mb-4"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#ccc #f1f1f1",
                }}
              >
                {/* Display Main Enquiry Comment First */}
                {selectedEnquiryForComments.comments && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs font-semibold text-blue-700 mb-0.5">
                      Primary Enquiry Comment:
                    </p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {selectedEnquiryForComments.comments}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Recorded on:{" "}
                      {formatDateTime(
                        selectedEnquiryForComments.updated_at ||
                          selectedEnquiryForComments.created_at
                      )}
                    </p>
                  </div>
                )}

                {isCommentsLoading ? (
                  <div className="flex justify-center items-center py-6">
                    <AiOutlineLoading3Quarters className="animate-spin text-blue-500 text-2xl" />
                  </div>
                ) : modalComments.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-600 mt-2 mb-1">
                      Comment History:
                    </p>
                    {modalComments.map((comment) => (
                      <div
                        key={comment.id}
                        className="bg-gray-50 p-3 rounded-lg border border-gray-100"
                      >
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {comment.comment}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-1">
                          {formatDateTime(comment.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4 text-sm">
                    {selectedEnquiryForComments.comments
                      ? "No additional comment history."
                      : "No comments recorded for this enquiry yet."}
                  </p>
                )}
              </div>

              {/* Add New Comment Area */}
              <div className="mt-auto pt-4 border-t border-gray-200 flex-shrink-0">
                <textarea
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  placeholder="Add a new comment..."
                  rows={3}
                  className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
                  disabled={isAddingComment}
                />
                <div className="flex justify-end">
                  <button
                    onClick={handleAddNewComment}
                    disabled={isAddingComment || !newCommentText.trim()}
                    className="px-4 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium flex items-center justify-center min-w-[100px] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isAddingComment ? (
                      <AiOutlineLoading3Quarters className="animate-spin h-4 w-4" />
                    ) : (
                      "Add Comment"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* --- END Comments History Modal --- */}

      <style jsx>{`
        @keyframes fadeUpAndOut {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
};

export default ProjectEnquiryTile;
